import { z } from 'zod';
import {
  dateTimeSchema,
  extraValuesSchema,
  idSchema,
  nullableDateTimeSchema,
} from './common';
import { audioStatusSchema, languageSchema } from './enums';

/** SPEC §2/§3/§6 — Row + AudioAsset + Take DTOs. */

export const takeSchema = z.object({
  id: idSchema,
  audioAssetId: idSchema,
  /** 1-based per asset. */
  index: z.number().int().min(1),
  stagingPath: z.string(),
  textSent: z.string(),
  textHash: z.string(),
  seed: z.number().int(),
  modelId: z.string(),
  voiceId: z.string(),
  voiceSettings: z.object({
    stability: z.number(),
    similarityBoost: z.number(),
    style: z.number(),
    useSpeakerBoost: z.boolean(),
    speed: z.number(),
  }),
  characterCount: z.number().int().min(0),
  requestId: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  durationMs: z.number().int().nullable(),
  createdById: idSchema,
  createdAt: dateTimeSchema,
  /** Public URL of the staging object, with `?v=` cache-bust when known. */
  url: z.string().nullable(),
});
export type Take = z.infer<typeof takeSchema>;

export const audioAssetSchema = z.object({
  id: idSchema,
  rowId: idSchema,
  language: languageSchema,
  status: audioStatusSchema,
  currentTakeId: idSchema.nullable(),
  approvedTakeId: idSchema.nullable(),
  generatedTextHash: z.string().nullable(),
  approvedTextHash: z.string().nullable(),
  publishedPath: z.string().nullable(),
  deliveredPath: z.string().nullable(),
  publishedGeneration: z.string().nullable(),
  attemptCount: z.number().int().min(0),
  claimedAt: nullableDateTimeSchema,
  /**
   * Set while a single-asset regeneration is in flight. It is deliberately NOT the
   * `status`: an APPROVED asset stays APPROVED (and keeps exporting its live URL)
   * while the replacement take is produced. The chip renders "approved + regenerating".
   */
  regeneratingAt: nullableDateTimeSchema,
  lastErrorCode: z.string().nullable(),
  lastErrorMessage: z.string().nullable(),
  runId: idSchema.nullable(),
  /** Server-computed: currentTake.textHash !== textHashFor(asset) (SPEC §3). */
  isStale: z.boolean(),
  /** Number of takes recorded so far — the UI nudges to the override after 3. */
  takeCount: z.number().int().min(0),
  currentTake: takeSchema.nullable(),
  /** Public URL of the delivered/published object (`?v={generation}`), null until approved. */
  publishedUrl: z.string().nullable(),
});
export type AudioAsset = z.infer<typeof audioAssetSchema>;

export const rowSchema = z.object({
  id: idSchema,
  batchId: idSchema,
  rowIndex: z.number().int().min(0),
  englishText: z.string(),
  arabicText: z.string(),
  originalEnglishText: z.string(),
  originalArabicText: z.string(),
  extraValues: extraValuesSchema,
  spokenOverrideEn: z.string().nullable(),
  spokenOverrideAr: z.string().nullable(),
  version: z.number().int().min(1),
  publishedSlug: z.string().nullable(),
  approvedAt: nullableDateTimeSchema,
  approvedById: idSchema.nullable(),
  approvedByName: z.string().nullable(),
  isManual: z.boolean(),
  removedAt: nullableDateTimeSchema,
  assets: z.object({
    EN: audioAssetSchema.nullable(),
    AR: audioAssetSchema.nullable(),
  }),
});
export type Row = z.infer<typeof rowSchema>;

/** GET /api/batches/:id/rows — always ORDER BY rowIndex ASC (SPEC §2). */
export const listRowsQuerySchema = z.object({
  status: audioStatusSchema.optional(),
  q: z.string().trim().max(500).optional(),
  includeRemoved: z.coerce.boolean().default(false),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(1000).default(500),
});
export type ListRowsQuery = z.infer<typeof listRowsQuerySchema>;

export const listRowsResponseSchema = z.object({
  rows: z.array(rowSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  hasMore: z.boolean(),
});
export type ListRowsResponse = z.infer<typeof listRowsResponseSchema>;

/** GET /api/rows/:id */
export const rowResponseSchema = z.object({ row: rowSchema });
export type RowResponse = z.infer<typeof rowResponseSchema>;

/**
 * PATCH /api/rows/:id — optimistic concurrency via `version`.
 * 409 `row_version_conflict` carries the current row + the editor's name.
 */
export const updateRowRequestSchema = z
  .object({
    version: z.number().int().min(1),
    englishText: z.string().max(5000).optional(),
    arabicText: z.string().max(5000).optional(),
  })
  .refine((v) => v.englishText !== undefined || v.arabicText !== undefined, {
    message: 'Provide englishText and/or arabicText',
  });
export type UpdateRowRequest = z.infer<typeof updateRowRequestSchema>;

export const updateRowResponseSchema = z.object({
  row: rowSchema,
  /** Languages whose audio went stale because of this edit. */
  staleLanguages: z.array(languageSchema),
});
export type UpdateRowResponse = z.infer<typeof updateRowResponseSchema>;

/** PATCH /api/rows/:id/override — the "Fix pronunciation" panel (SPEC §11). */
export const updateRowOverrideRequestSchema = z
  .object({
    version: z.number().int().min(1),
    spokenOverrideEn: z.string().max(5000).nullable().optional(),
    spokenOverrideAr: z.string().max(5000).nullable().optional(),
  })
  .refine((v) => v.spokenOverrideEn !== undefined || v.spokenOverrideAr !== undefined, {
    message: 'Provide spokenOverrideEn and/or spokenOverrideAr',
  });
export type UpdateRowOverrideRequest = z.infer<typeof updateRowOverrideRequestSchema>;

export const updateRowOverrideResponseSchema = updateRowResponseSchema;
export type UpdateRowOverrideResponse = z.infer<typeof updateRowOverrideResponseSchema>;

/** 409 body when `version` is stale. */
export const rowVersionConflictSchema = z.object({
  code: z.literal('row_version_conflict'),
  message: z.string(),
  currentVersion: z.number().int().min(1),
  changedByName: z.string().nullable(),
  row: rowSchema,
});
export type RowVersionConflict = z.infer<typeof rowVersionConflictSchema>;

/**
 * POST /api/batches/:id/rows — append a manual row (`isManual: true`).
 * SPEC-GAP: §2 defines `Row.isManual` and §7 orders manual rows after CSV rows,
 * but no endpoint is specified; this is the simplest consistent shape.
 */
export const createManualRowRequestSchema = z.object({
  englishText: z.string().trim().min(1).max(5000),
  arabicText: z.string().max(5000).default(''),
  extraValues: extraValuesSchema.default({}),
});
export type CreateManualRowRequest = z.infer<typeof createManualRowRequestSchema>;

export const createManualRowResponseSchema = z.object({ row: rowSchema });
export type CreateManualRowResponse = z.infer<typeof createManualRowResponseSchema>;

/** DELETE /api/rows/:id — soft remove (`removedAt`). */
export const removeRowResponseSchema = z.object({ row: rowSchema });
export type RemoveRowResponse = z.infer<typeof removeRowResponseSchema>;
